import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import InvestorLayout from '../components/InvestorLayout';
import investorService from '../services/investorService';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Textarea } from '../components/ui/textarea';
import { Heart, Eye, Edit2, Trash2, MapPin, Search } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const InvestorFavoritesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState('newest'); // newest | oldest

  // Dialog states
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [selectedFavorite, setSelectedFavorite] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [removingFavorite, setRemovingFavorite] = useState(false);

  const fetchFavorites = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const { data, error } = await investorService.getFavorites(user.id);

      if (error) throw error;

      setFavorites(data || []);
    } catch (err) {
      console.error('Favorites fetch error:', err);
      toast({
        title: t('investor.dashboard.error_title'),
        description: err.message || t('investor.dashboard.error_loading'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [user?.id, t]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  // Sıralanmış favoriler
  const sortedFavorites = [...favorites].sort((a, b) => {
    if (sortOrder === 'newest') {
      return new Date(b.created_at) - new Date(a.created_at);
    }
    return new Date(a.created_at) - new Date(b.created_at);
  });

  // Not ekleme/düzenleme dialog'unu aç
  const openNoteDialog = (favorite) => {
    setSelectedFavorite(favorite);
    setNoteText(favorite.notes || '');
    setNoteDialogOpen(true);
  };

  // Notu kaydet
  const handleSaveNote = async () => {
    if (!selectedFavorite) return;

    try {
      setSavingNote(true);
      const { error } = await investorService.addFavorite(
        user.id,
        selectedFavorite.property_id,
        noteText
      );

      if (error) throw error;

      // Listeyi güncelle
      setFavorites((prev) =>
        prev.map((fav) =>
          fav.id === selectedFavorite.id ? { ...fav, notes: noteText } : fav
        )
      );

      toast({
        title: t('investor.favorites.saveNote'),
        description: noteText
          ? t('investor.favorites.editNote')
          : t('investor.favorites.addNote'),
      });

      setNoteDialogOpen(false);
      setSelectedFavorite(null);
      setNoteText('');
    } catch (err) {
      console.error('Save note error:', err);
      toast({
        title: t('investor.dashboard.error_title'),
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setSavingNote(false);
    }
  };

  // Favoriden çıkarma dialog'unu aç
  const openRemoveDialog = (favorite) => {
    setSelectedFavorite(favorite);
    setRemoveDialogOpen(true);
  };

  // Favoriden çıkar
  const handleRemoveFavorite = async () => {
    if (!selectedFavorite) return;

    try {
      setRemovingFavorite(true);
      const { error } = await investorService.removeFavorite(
        user.id,
        selectedFavorite.property_id
      );

      if (error) throw error;

      // Listeden kaldır
      setFavorites((prev) => prev.filter((fav) => fav.id !== selectedFavorite.id));

      toast({
        title: t('investor.favorites.removed'),
        description: selectedFavorite.property?.title || 'Property',
      });

      setRemoveDialogOpen(false);
      setSelectedFavorite(null);
    } catch (err) {
      console.error('Remove favorite error:', err);
      toast({
        title: t('investor.dashboard.error_title'),
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setRemovingFavorite(false);
    }
  };

  // Skeleton bileşeni
  const FavoriteCardSkeleton = () => (
    <Card className="border border-gray-200">
      <CardContent className="p-4">
        <Skeleton className="w-full h-48 rounded-lg mb-4" />
        <Skeleton className="h-5 w-3/4 mb-2" />
        <Skeleton className="h-4 w-1/2 mb-3" />
        <div className="flex gap-2">
          <Skeleton className="h-9 flex-1" />
          <Skeleton className="h-9 w-9" />
          <Skeleton className="h-9 w-9" />
        </div>
      </CardContent>
    </Card>
  );

  // Tek bir favori kartı
  const FavoriteCard = ({ favorite }) => {
    const property = favorite.property;
    const thumbnail = property?.images?.[0] || null;

    return (
      <Card className="border border-gray-200 hover:border-[#D4AF37] hover:shadow-md transition-all duration-300">
        <CardContent className="p-4">
          {/* Thumbnail */}
          <div className="w-full h-48 bg-gray-200 rounded-lg overflow-hidden mb-4">
            {thumbnail ? (
              <img
                src={thumbnail}
                alt={property?.title || 'Property'}
                className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                onClick={() => navigate(`/tr/properties/${favorite.property_id}`)}
              />
            ) : (
              <div
                className="w-full h-full bg-gray-300 flex items-center justify-center cursor-pointer"
                onClick={() => navigate(`/tr/properties/${favorite.property_id}`)}
              >
                <Search size={48} className="text-gray-400" />
              </div>
            )}
          </div>

          {/* Bilgiler */}
          <h3 className="font-semibold text-[#0A192F] text-lg truncate mb-1">
            {property?.title || 'Property'}
          </h3>
          <div className="flex items-center text-sm text-gray-500 mb-2">
            <MapPin size={14} className="mr-1 flex-shrink-0" />
            <span className="truncate">{property?.location || '-'}</span>
          </div>

          {property?.price && (
            <p className="font-bold text-[#D4AF37] text-xl mb-3">
              £{Number(property.price).toLocaleString()}
            </p>
          )}

          {/* Eklenme tarihi */}
          <div className="flex items-center text-xs text-gray-500 mb-3">
            <span>
              {t('investor.favorites.addedOn')}:{' '}
              {new Date(favorite.created_at).toLocaleDateString()}
            </span>
          </div>

          {/* Not (varsa) */}
          {favorite.notes && (
            <div className="bg-gray-50 rounded-lg p-2 mb-3 text-sm text-gray-700">
              <span className="font-medium">{t('investor.portfolio.notes')}:</span>{' '}
              {favorite.notes}
            </div>
          )}

          {/* Aksiyon butonları */}
          <div className="flex gap-2">
            <Button
              size="sm"
              className="flex-1 bg-[#0A192F] text-white hover:bg-[#112240]"
              onClick={() => navigate(`/tr/properties/${favorite.property_id}`)}
            >
              <Eye size={16} className="mr-1" />
              {t('investor.favorites.viewProperty')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white"
              onClick={() => openNoteDialog(favorite)}
            >
              <Edit2 size={16} />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              onClick={() => openRemoveDialog(favorite)}
            >
              <Heart size={16} fill="currentColor" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <InvestorLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading text-[#0A192F] mb-2">
              {t('investor.favorites.title')}
            </h1>
            <p className="text-gray-600">
              {favorites.length}{' '}
              {favorites.length === 1 ? 'favori ilan' : 'favori ilan'}
            </p>
          </div>

          {/* Sıralama */}
          {favorites.length > 0 && (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={sortOrder === 'newest' ? 'default' : 'outline'}
                onClick={() => setSortOrder('newest')}
                className={
                  sortOrder === 'newest'
                    ? 'bg-[#0A192F] text-white'
                    : 'border-gray-300 text-gray-600'
                }
              >
                {t('investor.favorites.sortNewest')}
              </Button>
              <Button
                size="sm"
                variant={sortOrder === 'oldest' ? 'default' : 'outline'}
                onClick={() => setSortOrder('oldest')}
                className={
                  sortOrder === 'oldest'
                    ? 'bg-[#0A192F] text-white'
                    : 'border-gray-300 text-gray-600'
                }
              >
                {t('investor.favorites.sortOldest')}
              </Button>
            </div>
          )}
        </div>

        {/* Favori Listesi */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FavoriteCardSkeleton />
            <FavoriteCardSkeleton />
            <FavoriteCardSkeleton />
          </div>
        ) : sortedFavorites.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedFavorites.map((favorite) => (
              <FavoriteCard key={favorite.id} favorite={favorite} />
            ))}
          </div>
        ) : (
          <Card className="border border-gray-200">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <Heart size={56} className="text-gray-300 mb-4" />
              <h3 className="text-xl font-heading text-[#0A192F] mb-2">
                {t('investor.favorites.noFavorites')}
              </h3>
              <Button
                className="mt-4 bg-[#D4AF37] text-white hover:bg-[#B5952F]"
                onClick={() => navigate('/tr')}
              >
                {t('investor.favorites.exploreListing')}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Not Ekleme/Düzenleme Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedFavorite?.notes
                ? t('investor.favorites.editNote')
                : t('investor.favorites.addNote')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                {selectedFavorite?.property?.title || 'Property'}
              </p>
              <Textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder={t('investor.favorites.notePlaceholder')}
                rows={5}
                className="resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNoteDialogOpen(false)}
              disabled={savingNote}
            >
              {t('common.cancel') || 'İptal'}
            </Button>
            <Button
              onClick={handleSaveNote}
              disabled={savingNote}
              className="bg-[#D4AF37] text-white hover:bg-[#B5952F]"
            >
              {savingNote ? t('common.saving') || 'Kaydediliyor...' : t('investor.favorites.saveNote')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Favoriden Çıkarma Onay Dialog */}
      <AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('investor.favorites.removeTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('investor.favorites.removeConfirm')}
              <br />
              <span className="font-semibold text-gray-900 mt-2 inline-block">
                {selectedFavorite?.property?.title || 'Property'}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removingFavorite}>
              {t('common.cancel') || 'İptal'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveFavorite}
              disabled={removingFavorite}
              className="bg-red-500 hover:bg-red-600"
            >
              {removingFavorite ? t('common.removing') || 'Kaldırılıyor...' : t('investor.favorites.removeTitle')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </InvestorLayout>
  );
};

export default InvestorFavoritesPage;
